/**
 * The map grid: terrain, passability, cliffs, occupancy, and regolith.
 *
 * One cell is `1.0` in Q16.16, that is 65536 raw sub-units, so the cell index
 * of a world coordinate is `v >> 16` and sub-cell position carries 16 bits of
 * precision (KTD1).
 */

import type { Ctx, Subsystem } from '../core/registry.js'
import type { Fixed } from '../core/fixed.js'
import { MAP_SIZE } from '../core/config.js'
import { NO_ENTITY } from './entities.js'

export const TERRAIN = {
  Regolith: 0,
  Rock: 1,
  Dust: 2,
  Basalt: 3,
  Crater: 4,
  /** Frozen brine. Impassable, and the only cold surface on the map. */
  Ice: 5,
  /** Terraformed growth along the ice fringe. Ordinary ground, green. */
  Verdant: 6,
  /**
   * Gems. The same substance to a harvester and worth several times as much
   * per load, in much smaller and rarer patches.
   */
  Gems: 7,
} as const

/** Credits a unit of each terrain's ore is worth. Ordinary ground yields none. */
export const ORE_VALUE: Record<number, number> = { 0: 1, 7: 4 }

export const CELL = {
  Passable: 1 << 0,
  Cliff: 1 << 1,
  /** Holds regolith, whether or not it is currently depleted. */
  Field: 1 << 2,
  /** A slope joining two elevation levels. Passable; never a wall. */
  Ramp: 1 << 3,
  /** Ice. Impassable and unbuildable, but not a wall — nothing stands on it. */
  Water: 1 << 4,
  /** Static scenery. Blocks movement and construction, independently of cliffs. */
  Scenery: 1 << 5,
} as const

export const GROUND = { Natural: 0, Road: 1, Paving: 2, Parking: 3 } as const
export const SCENERY = { None: 0, Tree: 1, House: 2, Warehouse: 3, Cargo: 4, Tank: 5 } as const

/** Odd footprints centered on the scenery's anchor cell. */
export function sceneryWidth(kind: number): number {
  return kind === SCENERY.House || kind === SCENERY.Warehouse ? 3 : 1
}

/** Distinct elevation levels, 0 to `LEVELS - 1`. */
export const LEVELS = 3

/** Regolith units in a full cell. A harvester's capacity is a fraction of this. */
export const REGOLITH_FULL = 1000

export class World implements Subsystem {
  static id = 'world'

  readonly size = MAP_SIZE
  readonly cells = MAP_SIZE * MAP_SIZE

  readonly terrain: Uint8Array
  readonly flags: Uint8Array
  readonly regolith: Int32Array
  /** Entity id occupying each cell, or NO_ENTITY. Structures fill their footprint. */
  readonly occupancy: Uint32Array
  /**
   * Elevation level per cell, `0` to `LEVELS - 1`.
   *
   * Levels are a *gameplay* structure, not decoration: movement between cells
   * of different levels is blocked by the wall the boundary produces, and the
   * only way up is a ramp. Pathfinding needs no rule for it — the wall cells
   * are simply impassable, which the flow field already understands — so the
   * whole feature costs the simulation one derivation pass in mapgen.
   */
  readonly height: Uint8Array
  readonly ground: Uint8Array
  /** Nonzero only at an object's center; CELL.Scenery covers its full footprint. */
  readonly scenery: Uint8Array

  /** Player start positions in cell coordinates, filled by mapgen. */
  readonly starts: Int32Array

  constructor() {
    this.terrain = new Uint8Array(this.cells)
    this.flags = new Uint8Array(this.cells)
    this.regolith = new Int32Array(this.cells)
    this.occupancy = new Uint32Array(this.cells)
    this.height = new Uint8Array(this.cells)
    this.ground = new Uint8Array(this.cells)
    this.scenery = new Uint8Array(this.cells)
    this.starts = new Int32Array(8).fill(-1)
    this.flags.fill(CELL.Passable)
  }

  init(ctx: Ctx): void {
    ctx.hash.register('world:terrain', this.terrain)
    ctx.hash.register('world:flags', this.flags)
    ctx.hash.register('world:regolith', this.regolith)
    ctx.hash.register('world:occupancy', this.occupancy)
    ctx.hash.register('world:height', this.height)
    ctx.hash.register('world:ground', this.ground)
    ctx.hash.register('world:scenery', this.scenery)
    ctx.hash.register('world:starts', this.starts)
  }

  // --- indexing ------------------------------------------------------------

  index(cx: number, cy: number): number {
    return cy * this.size + cx
  }

  inBounds(cx: number, cy: number): boolean {
    return cx >= 0 && cy >= 0 && cx < this.size && cy < this.size
  }

  /** Cell index for a world position, or -1 if outside the map. */
  cellAt(x: Fixed, y: Fixed): number {
    const cx = x >> 16
    const cy = y >> 16
    return this.inBounds(cx, cy) ? cy * this.size + cx : -1
  }

  /** Centre of a cell in world space. */
  cellCenterX(cx: number): Fixed {
    return (((cx << 16) | 0) + 32768) as Fixed
  }

  cellCenterY(cy: number): Fixed {
    return (((cy << 16) | 0) + 32768) as Fixed
  }

  // --- terrain queries -----------------------------------------------------

  isPassable(cx: number, cy: number): boolean {
    if (!this.inBounds(cx, cy)) return false
    return (this.flags[cy * this.size + cx] & CELL.Passable) !== 0
  }

  isCliff(cx: number, cy: number): boolean {
    if (!this.inBounds(cx, cy)) return true
    return (this.flags[cy * this.size + cx] & CELL.Cliff) !== 0
  }

  isWater(cx: number, cy: number): boolean {
    if (!this.inBounds(cx, cy)) return false
    return (this.flags[cy * this.size + cx] & CELL.Water) !== 0
  }

  isRamp(cx: number, cy: number): boolean {
    if (!this.inBounds(cx, cy)) return false
    return (this.flags[cy * this.size + cx] & CELL.Ramp) !== 0
  }

  /** Elevation level, clamped at the border so off-map reads as the rim. */
  levelAt(cx: number, cy: number): number {
    if (!this.inBounds(cx, cy)) return LEVELS - 1
    return this.height[cy * this.size + cx]
  }

  isFree(cx: number, cy: number): boolean {
    if (!this.inBounds(cx, cy)) return false
    const i = cy * this.size + cx
    return (this.flags[i] & CELL.Passable) !== 0 && this.occupancy[i] === NO_ENTITY
  }

  occupantAt(cx: number, cy: number): number {
    if (!this.inBounds(cx, cy)) return NO_ENTITY
    return this.occupancy[cy * this.size + cx]
  }

  setOccupant(cx: number, cy: number, id: number): void {
    if (!this.inBounds(cx, cy)) return
    this.occupancy[cy * this.size + cx] = id
  }

  setCliff(cx: number, cy: number, on: boolean): void {
    if (!this.inBounds(cx, cy)) return
    const i = cy * this.size + cx
    if (on) {
      this.flags[i] |= CELL.Cliff
      this.flags[i] &= ~CELL.Passable
      this.height[i] = 1
    } else {
      this.flags[i] &= ~CELL.Cliff
      this.flags[i] |= CELL.Passable
      this.height[i] = 0
    }
  }

  setPassable(cx: number, cy: number, on: boolean): void {
    if (!this.inBounds(cx, cy)) return
    const i = cy * this.size + cx
    if (on) this.flags[i] |= CELL.Passable
    else this.flags[i] &= ~CELL.Passable
  }

  // --- structure footprints ------------------------------------------------

  /**
   * Claim a `w × h` footprint anchored at its top-left cell.
   * Returns false without partial mutation if any cell is unavailable.
   */
  occupyFootprint(cx: number, cy: number, w: number, h: number, id: number): boolean {
    for (let y = cy; y < cy + h; y++) {
      for (let x = cx; x < cx + w; x++) {
        if (!this.inBounds(x, y)) return false
        const i = y * this.size + x
        if ((this.flags[i] & CELL.Passable) === 0) return false
        if (this.occupancy[i] !== NO_ENTITY && this.occupancy[i] !== id) return false
      }
    }
    for (let y = cy; y < cy + h; y++) {
      for (let x = cx; x < cx + w; x++) {
        const i = y * this.size + x
        this.occupancy[i] = id
        this.flags[i] &= ~CELL.Passable
      }
    }
    return true
  }

  /** Release a footprint, restoring passability. Called on structure death. */
  freeFootprint(cx: number, cy: number, w: number, h: number): void {
    for (let y = cy; y < cy + h; y++) {
      for (let x = cx; x < cx + w; x++) {
        if (!this.inBounds(x, y)) continue
        const i = y * this.size + x
        this.occupancy[i] = NO_ENTITY
        // A cliff stays impassable; only the structure's own claim is released.
        if ((this.flags[i] & (CELL.Cliff | CELL.Scenery | CELL.Water)) === 0) this.flags[i] |= CELL.Passable
      }
    }
  }

  /**
   * True if any occupant matching `predicate` sits within `radius` cells of the
   * footprint, measured from footprint edge to occupant cell.
   *
   * The whole expanded rectangle is scanned rather than its border, because a
   * structure inside the radius may not touch the border at all. At a radius of
   * four and a 3×3 footprint that is 121 cells, which is cheap next to how
   * rarely placement is attempted.
   */
  hasOccupantWithin(
    cx: number,
    cy: number,
    w: number,
    h: number,
    radius: number,
    predicate: (id: number) => boolean,
  ): boolean {
    for (let y = cy - radius; y < cy + h + radius; y++) {
      if (y < 0 || y >= this.size) continue
      for (let x = cx - radius; x < cx + w + radius; x++) {
        if (x < 0 || x >= this.size) continue
        if (predicate(this.occupancy[y * this.size + x])) return true
      }
    }
    return false
  }

  // --- regolith ------------------------------------------------------------

  regolithAt(cx: number, cy: number): number {
    if (!this.inBounds(cx, cy)) return 0
    return this.regolith[cy * this.size + cx]
  }

  /**
   * Remove up to `amount` regolith. Returns what was actually taken.
   * A cell mined to zero is marked depleted and becomes passable again.
   */
  /**
   * What a unit of ore from this cell is worth.
   *
   * Read at the moment of mining rather than at the moment of unloading,
   * because by then the cell may be empty and its terrain reset — the value
   * has to travel with the load, not be looked up again later.
   */
  oreValueAt(cx: number, cy: number): number {
    if (!this.inBounds(cx, cy)) return 0
    return ORE_VALUE[this.terrain[cy * this.size + cx]] ?? 1
  }

  mine(cx: number, cy: number, amount: number): number {
    if (!this.inBounds(cx, cy)) return 0
    const i = cy * this.size + cx
    const have = this.regolith[i]
    if (have <= 0) return 0
    const taken = have < amount ? have : amount
    this.regolith[i] = have - taken
    if (this.regolith[i] === 0) {
      this.terrain[i] = TERRAIN.Dust
      if ((this.flags[i] & CELL.Cliff) === 0) this.flags[i] |= CELL.Passable
    }
    return taken
  }

  isDepleted(cx: number, cy: number): boolean {
    if (!this.inBounds(cx, cy)) return true
    const i = cy * this.size + cx
    return (this.flags[i] & CELL.Field) === 0 || this.regolith[i] <= 0
  }

  /** Total regolith left on the map. Used by the economy and the AI. */
  totalRegolith(): number {
    let sum = 0
    for (let i = 0; i < this.cells; i++) sum += this.regolith[i]
    return sum
  }

  reset(): void {
    this.terrain.fill(0)
    this.flags.fill(CELL.Passable)
    this.regolith.fill(0)
    this.occupancy.fill(NO_ENTITY)
    this.height.fill(0)
    this.ground.fill(0)
    this.scenery.fill(0)
    this.starts.fill(-1)
  }
}
